import { LitElement, css, html } from 'lit'

export class CtTermCard extends LitElement {
  static properties = {
    date: { type: String },
    group: { type: String },
    airport: { type: String },
    status: { type: String },
    price: { type: String },

    flightPrice: {
      type: String,
      attribute: 'flight-price',
    },

    priceNote: {
      type: String,
      attribute: 'price-note',
    },

    statusVariant: {
      type: String,
      attribute: 'status-variant',
    },

    bookingEnabled: {
      type: Boolean,
      attribute: 'booking-enabled',
    },

    bookingUrl: {
      type: String,
      attribute: 'booking-url',
    },

    bookingLabel: {
      type: String,
      attribute: 'booking-label',
    },

    pilotName: {
      type: String,
      attribute: 'pilot-name',
    },

    pilotImage: {
      type: String,
      attribute: 'pilot-image',
    },
  }

  constructor() {
    super()

    this.date = ''
    this.group = ''
    this.airport = ''
    this.status = ''
    this.price = ''
    this.flightPrice = ''
    this.priceNote = ''

    this.statusVariant = 'unknown'

    this.bookingEnabled = false
    this.bookingUrl = ''
    this.bookingLabel = 'Rezerwacja niedostępna'

    this.pilotName = ''
    this.pilotImage = ''
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      max-width: 420px;
      font-family: Poppins, Arial, sans-serif;
      color: #404040;
    }

    * {
      box-sizing: border-box;
    }

    .card {
      overflow: hidden;
      border-radius: 14px;
      background: #ffffff;
      box-shadow: 0 5px 18px rgba(43, 52, 60, 0.14);
    }

    .date {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 18px;
      background: #819fbe;
      color: #ffffff;
      font-size: 26px;
      font-weight: 500;
    }

    .season-icon {
      font-size: 30px;
    }

    .summary {
      display: grid;
      grid-template-columns: 110px minmax(0, 1fr);
      gap: 14px;
      padding: 17px 16px 15px 0;
    }

    .pilot {
      display: flex;
      min-height: 160px;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      border-radius: 0 34px 34px 0;
      background:
        linear-gradient(
          135deg,
          #d9dee3,
          #f1f3f5
        );
      color: #777777;
      font-size: 14px;
    }

    .pilot img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }

    .meta {
      display: flex;
      min-width: 0;
      flex-direction: column;
      justify-content: center;
      gap: 13px;
    }

    .pill {
      display: flex;
      min-width: 0;
      min-height: 55px;
      align-items: center;
      gap: 11px;
      padding: 7px 14px 7px 9px;
      border: 1px solid #d9e0e8;
      border-radius: 999px;
      font-family: Georgia, serif;
      font-size: 19px;
    }

    .pill-icon {
      display: grid;
      flex: 0 0 auto;
      place-items: center;
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: #f3f8fa;
      color: #18586a;
    }

    .details {
      display: flex;
      min-height: 68px;
      align-items: center;
      gap: 12px;
      padding: 12px 20px;
      color: #18586a;
      font-size: 20px;
    }

    .arrow {
      margin-left: auto;
      font-size: 28px;
    }

    .status {
      padding: 11px 16px;
      text-align: center;
      text-transform: uppercase;
      font-size: 19px;
      font-weight: 500;
    }

    .status--available {
      color: #4a9e57;
      background: #f0f8f1;
    }

    .status--last-place {
      color: #c62828;
      background: #fdecec;
    }

    .status--last-places {
      color: #d67313;
      background: #fff3e5;
    }

    .status--sold-out {
      color: #777777;
      background: #eeeeee;
    }

    .status--unknown {
      color: #666666;
      background: #f1f1f1;
    }

    .price-area {
      padding: 14px 17px 17px;
    }

    .price {
      color: #48a158;
      font-size: 29px;
      font-weight: 600;
      line-height: 1.1;
    }

    .flight-price {
      margin-top: 3px;
      font-size: 17px;
      line-height: 1.35;
    }

    .price-note {
      margin-top: 9px;
      color: #8f949c;
      font-size: 13px;
      line-height: 1.4;
    }

    .booking {
      display: flex;
      min-height: 56px;
      margin-top: 12px;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 10px;
      background: #f7931e;
      color: #ffffff;
      text-decoration: none;
      text-transform: uppercase;
      font: inherit;
      font-size: 20px;
      font-weight: 700;
      cursor: pointer;
    }

    .booking:hover {
      background: #ffac4d;
    }

    .booking:disabled {
      background: #b8b8b8;
      cursor: not-allowed;
    }
  `

  render() {
    return html`
      <article class="card">
        <header class="date">
          <span class="season-icon">❄</span>
          <span>${this.date}</span>
        </header>

        <section class="summary">
          <div class="pilot">
            ${this.pilotImage
              ? html`
                  <img
                    src=${this.pilotImage}
                    alt=${this.pilotName
                      ? `Pilot wyjazdu - ${this.pilotName}`
                      : 'Pilot wyjazdu'}
                  >
                `
              : html`
                  <span>
                    ${this.pilotName || 'Pilot wkrótce'}
                  </span>
                `}
          </div>

          <div class="meta">
            <div class="pill">
              <span class="pill-icon">👥</span>
              <span>${this.group}</span>
            </div>

            <div class="pill">
              <span class="pill-icon">✈</span>
              <span>${this.airport}</span>
            </div>
          </div>
        </section>

        <div class="details">
          <span>☷</span>
          <span>Sprawdź szczegóły terminu</span>
          <span class="arrow">⌄</span>
        </div>

        <div class="status status--${this.statusVariant}">
          ${this.status}
        </div>

        <section class="price-area">
          <div class="price">
            ${this.price}
          </div>

          ${this.flightPrice
            ? html`
                <div class="flight-price">
                  ${this.flightPrice}
                </div>
              `
            : ''}

          ${this.priceNote
            ? html`
                <div class="price-note">
                  ${this.priceNote}
                </div>
              `
            : ''}

          ${this.bookingEnabled && this.bookingUrl
            ? html`
                <a
                  class="booking"
                  href=${this.bookingUrl}
                >
                  Rezerwuj
                </a>
              `
            : html`
                <button
                  class="booking"
                  type="button"
                  disabled
                >
                  ${this.bookingLabel}
                </button>
              `}
        </section>
      </article>
    `
  }
}

if (!customElements.get('ct-term-card')) {
  customElements.define(
    'ct-term-card',
    CtTermCard
  )
}